import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CustomAutoCompleteComponent } from 'app/core/components/custom-auto-complete/custom-auto-complete.component';
import { DocumentKind } from 'app/core/constants/document-center.constants';
import { OverlayLoaderDirective } from 'app/core/directives/overlay-loader.directive';
import { GlobalService } from 'app/core/global.service';
import { formatDateForPayload } from 'app/core/utils/date.utils';
import { IdempotencyAction } from 'app/core/utils/idempotency.util';
import { ChartOfAccountsService } from 'app/modules/accounting/chart-of-accounts/chart-of-accounts.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ScbMyBillingPaymentMethod } from '../scb.enums';
import { ScbApiService } from '../scb-api.service';
import { ScbPayMyBillingInvoiceRequest } from '../scb.models';
import { extractScbData } from '../scb-ui.utils';

export type ScbPayInvoiceDialogData = {
  salesInvoiceId: string;
  invoiceNumber?: string | null;
  amount?: number | null;
  remainingAmount?: number | null;
};

type CoaAccount = {
  id: string;
  accountCode?: string;
  accountName?: string;
  name?: string;
  code?: string;
};

type UploadResponse = { data?: { id?: string } };

const PARENT_CASH = '001-001-001';
const PARENT_BANK = '001-001-002';

@Component({
  selector: 'app-scb-pay-invoice-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogActions,
    MatDialogContent,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    CustomAutoCompleteComponent,
    OverlayLoaderDirective,
  ],
  templateUrl: './scb-pay-invoice-dialog.component.html',
  styles: `::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface { padding: 0 !important; }`,
})
export class ScbPayInvoiceDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ScbApiService);
  private readonly coa = inject(ChartOfAccountsService);
  private readonly globalService = inject(GlobalService);
  private readonly toast = inject(ToastrService);
  private readonly dialogRef = inject(MatDialogRef<ScbPayInvoiceDialogComponent, boolean>);
  private readonly data = inject<ScbPayInvoiceDialogData>(MAT_DIALOG_DATA);
  private readonly idempotency = new IdempotencyAction();

  readonly loadingLookups = signal(true);
  readonly saving = signal(false);
  readonly uploading = signal(false);
  readonly fileName = signal<string | null>(null);

  expenseAccounts: CoaAccount[] = [];
  cashAccounts: CoaAccount[] = [];
  bankAccounts: CoaAccount[] = [];

  readonly paymentMethodCash = ScbMyBillingPaymentMethod.Cash;

  readonly paymentMethodOptions: { value: number; label: string }[] = [
    { value: ScbMyBillingPaymentMethod.Cash, label: 'Cash' },
    { value: ScbMyBillingPaymentMethod.Bank, label: 'Bank' },
  ];

  readonly form = this.fb.group({
    expenseAccountId: this.fb.control<string | null>(null, Validators.required),
    paymentMethod: this.fb.control<number>(ScbMyBillingPaymentMethod.Bank, Validators.required),
    paymentAccountId: this.fb.control<string | null>(null, Validators.required),
    amount: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
    paymentDate: this.fb.control<Date | null>(new Date(), Validators.required),
    remarks: this.fb.control<string | null>(null),
    fileAttachmentId: this.fb.control<string | null>(null),
  });

  idCompare = (a: unknown, b: unknown): boolean => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return String(a) === String(b);
  };

  accountOptionLabel = (row: unknown): string => this.accountLabel(row as CoaAccount | null);

  displayExpenseFn = (id: unknown): string =>
    id == null ? '' : this.accountLabel(this.expenseAccounts.find((a) => String(a.id) === String(id)) ?? null);

  displayPaymentAccountFn = (id: unknown): string =>
    id == null
      ? ''
      : this.accountLabel(this.paymentAccounts.find((a) => String(a.id) === String(id)) ?? null);

  displayPaymentMethodFn = (value: unknown): string => {
    if (value == null) return '';
    return this.paymentMethodOptions.find((o) => o.value === value)?.label ?? '';
  };

  paymentMethodCompare = (a: unknown, b: unknown): boolean =>
    a === b || (a == null && b == null);

  get paymentAccounts(): CoaAccount[] {
    return this.form.get('paymentMethod')?.value === ScbMyBillingPaymentMethod.Cash
      ? this.cashAccounts
      : this.bankAccounts;
  }

  get titleHint(): string {
    return this.data.invoiceNumber?.trim() || 'Invoice';
  }

  ngOnInit(): void {
    const defaultAmount = this.data.remainingAmount ?? this.data.amount ?? null;
    if (defaultAmount != null) {
      this.form.patchValue({ amount: Number(defaultAmount) });
    }

    this.form
      .get('paymentMethod')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.patchValue({ paymentAccountId: null });
      });

    this.loadLookups();
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.uploading.set(true);
    this.globalService
      .uploadFile<UploadResponse>(file, DocumentKind.ExpenseVoucher)
      .pipe(
        finalize(() => this.uploading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          const id = res?.data?.id;
          if (!id) {
            this.toast.error('File upload failed');
            return;
          }
          this.form.patchValue({ fileAttachmentId: id });
          this.fileName.set(file.name);
        },
        error: () => this.toast.error('File upload failed'),
      });
    input.value = '';
  }

  removeFile(): void {
    this.form.patchValue({ fileAttachmentId: null });
    this.fileName.set(null);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const paymentDate = formatDateForPayload(raw.paymentDate);
    if (!paymentDate) {
      this.toast.error('Payment date is required');
      return;
    }
    const remarks = raw.remarks?.trim();
    const body: ScbPayMyBillingInvoiceRequest = {
      salesInvoiceId: this.data.salesInvoiceId,
      expenseAccountId: String(raw.expenseAccountId),
      paymentAccountId: String(raw.paymentAccountId),
      paymentMethod: raw.paymentMethod ?? ScbMyBillingPaymentMethod.Bank,
      amount: Number(raw.amount),
      paymentDate,
      fileAttachmentId: raw.fileAttachmentId,
      remarks: remarks ? remarks : null,
    };

    this.saving.set(true);
    this.api
      .payMyBillingInvoice(body, this.idempotency.getKey())
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (resp) => {
          if (resp?.success !== false) {
            this.idempotency.reset();
            this.toast.success(resp?.message || 'Payment submitted');
            this.dialogRef.close(true);
          } else {
            this.toast.error(resp?.message || 'Unable to pay invoice');
          }
        },
        error: (err: HttpErrorResponse) => {
          this.toast.error(err?.error?.message || 'Unable to pay invoice');
        },
      });
  }

  private loadLookups(): void {
    this.loadingLookups.set(true);
    forkJoin({
      expense: this.coa.getChartOfAccountsByType('5'),
      cash: this.coa.getAllChartOfAccounts(undefined, undefined, true, PARENT_CASH),
      bank: this.coa.getAllChartOfAccounts(undefined, undefined, true, PARENT_BANK),
    })
      .pipe(
        finalize(() => this.loadingLookups.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ expense, cash, bank }) => {
          this.expenseAccounts = this.unwrapAccounts(expense);
          this.cashAccounts = this.unwrapAccounts(cash);
          this.bankAccounts = this.unwrapAccounts(bank);
        },
        error: () => {
          this.toast.error('Unable to load accounts');
        },
      });
  }

  private unwrapAccounts(resp: unknown): CoaAccount[] {
    const data = extractScbData<CoaAccount[]>(resp) ?? resp;
    return Array.isArray(data) ? data : [];
  }

  private accountLabel(acc: CoaAccount | null): string {
    if (!acc) return '';
    const code = acc.accountCode || acc.code || '';
    const name = acc.accountName || acc.name || '';
    return [code, name].filter((p) => !!p).join(' ').trim() || String(acc.id);
  }
}
